/**
 * ScreenStamp — the screen's reference number, in the corner of every session screen.
 *
 * This is for bug reports, not for the person in the session: Andy or Marta
 * screenshots a screen and says "the problem is on 3.2". It replaced the
 * hand-typed "2 of 4" lines that used to sit above the Continue button.
 *
 * Why a fixed corner stamp (Lucy's placement, 2026-10-07):
 * - Beside the phase name it read as "step 3.2 of See", which is progress, and
 *   the full-screen overlays (the phase transition, the Satori moment, the
 *   insight card) cover the header, so it vanished on exactly those screens.
 * - Above the Continue button is where page counters live, so it read as one,
 *   and screens with no button had nowhere to put it.
 * - Small monospace digits pinned to a corner read as a build stamp. It sits
 *   above every overlay so it is in every screenshot.
 *
 * It is drawn here and nowhere else. Screens only report which screen they are
 * (lib/useScreenId.ts), so a screen cannot forget to show its number.
 *
 * Deliberately not translated, not animated, and hidden from screen readers:
 * it is a reference code, and reading "three point two" aloud to someone in a
 * session would be noise.
 */
import { useCurrentScreenId } from '../../lib/useScreenId'

interface ScreenStampProps {
  /**
   * The phase a hand-over is heading to, while the between-phase screen is up.
   * That screen belongs to no single numbered screen, so the stamp shows where
   * it leads ("→4") instead of the number of the screen underneath it.
   */
  transitioningTo?: number | null
}

export default function ScreenStamp({ transitioningTo = null }: ScreenStampProps) {
  const id = useCurrentScreenId()
  const text = transitioningTo !== null ? `→${transitioningTo}` : id
  if (!text) return null

  return (
    <div
      data-testid="screen-stamp"
      aria-hidden="true"
      style={{
        position: 'fixed',
        // Inside the header's top padding, above the pause button.
        top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
        right: 10,
        // Above the between-phase screen (z-50), the highest overlay in a session.
        zIndex: 60,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontVariantNumeric: 'tabular-nums',
        fontSize: 10,
        lineHeight: '12px',
        letterSpacing: '0.04em',
        // One grey that stays legible on the light pages and the dark overlays.
        color: '#9B8FAA',
        opacity: 0.8,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {text}
    </div>
  )
}
